import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { SelectItem } from 'primeng';
import { RouteRecommendationService } from '../../../services/route-recommendation.service';
import { FormControl, FormGroup } from '@angular/forms';
import {
  IRouteRecommendation,
  IRouteRecommendationSearch,
  IRouteRecommendationSearchFilters,
} from '../../../interfaces/route-recommendations/route-recommendation';
import { IUser } from '../../../interfaces/user';
import { SettingsService, UserService } from '../../../services';
import {
  IRouteHiddenReasonSelection,
  ISetRouteHidden,
} from '../../../interfaces/route-recommendations/route-rejection-reason';
import { MapControlTask } from '../../../components/map-control/map-control.component';
import { ControlsOfFlat } from '../../../utils/controls-of';
import localforage from 'localforage';
import { addDays } from 'date-fns';
import { Subscription } from 'rxjs';
import { remove } from 'lodash';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { first } from 'rxjs/operators';
import { customCalendarCellClass } from '../../../utils/calendar-utils';
import { ISystemSettings } from '../../../interfaces/system-settings';
import { RoutesAnalyticsService } from '../../../services/product-analytics/routes-analytics.service';
import { SupplierTravelPreferencesService } from '../../../services/supplier-travel-preferences.service';
import { RouteScheduleClashSeverity } from '../../../enums/route-schedule-clash-severity.enum';

export enum RouteFilterType {
  Available = 0,
  Grabbed = 1,
  Hidden = 2,
  Watched = 3,
  Details = 4,
}

enum PlannedRouteExtenderFilter {
  AllRoutes = 1,
  OnlyExtensions = 2,
  OnlyRoutes = 3,
}

interface IRouteRecommendationSearchFilterValues {
  noCarryOvers: boolean;
  dateRange: string[];
  dedupeRoutes: boolean;
  maxNumberOfTasks: number | null;
  component: RouteFilterType;
  sortBy: PlannedRouteSortFieldValue;
  extender: PlannedRouteExtenderFilter;
  extenderSelectedJobs: number[] | null;
  supplierId: number | null;
  jobNumberOrVehicleReg: string;
  excludeClashSeverities: RouteScheduleClashSeverity[];
}

enum PlannedRouteSortField {
  PoundPerHour = 'PoundPerHour',
  NetEarnings = 'NetEarnings',
  TotalEarnings = 'TotalEarnings',
  TotalTime = 'TotalTime',
  StartDate = 'StartDate',
}

export enum SortOrder {
  Desc = -1,
  Asc = 1,
}

interface PlannedRouteSortFieldValue {
  sortField: PlannedRouteSortField;
  sortOrder: SortOrder;
  label: string;
}

const ClickedRoutesStorageKey = 'clicked-routes';

@Component({
  selector: 'planned-routes-tab',
  templateUrl: './planned-routes-tab.component.html',
  styleUrls: ['./planned-routes-tab.component.scss'],
})
export class PlannedRoutesTabComponent implements OnInit, AfterViewInit, OnDestroy {
  public PlannedRouteExtenderFilter = PlannedRouteExtenderFilter;

  public showHideReasonDialog: boolean = false;

  @Output() public onHideClicked: EventEmitter<string> = new EventEmitter<string>();

  @Output() public totalChange: EventEmitter<number> = new EventEmitter<number>();

  private routeToHideId: number;
  private soloMapTaskId: number = null;

  private _total: number;
  private subscriptions: Subscription[] = [];

  public get total(): number {
    return this._total;
  }

  public set total(value: number) {
    this._total = value;
    this.totalChange.emit(value);
  }

  @Input() routeFilterType: RouteFilterType;

  @Input() taskScopeDays: number;

  @Output() routeListUpdate: EventEmitter<IRouteRecommendation[]> = new EventEmitter();

  @Input() isVME: boolean = false;

  sortByOptions: PlannedRouteSortFieldValue[] = [
    {
      label: 'Pound Per Hour',
      sortField: PlannedRouteSortField.PoundPerHour,
      sortOrder: SortOrder.Desc,
    },
    {
      label: 'Net Earnings',
      sortField: PlannedRouteSortField.NetEarnings,
      sortOrder: SortOrder.Desc,
    },
    {
      label: 'Total Earnings',
      sortField: PlannedRouteSortField.TotalEarnings,
      sortOrder: SortOrder.Desc,
    },
    {
      label: 'Total Time (Short To Long)',
      sortField: PlannedRouteSortField.TotalTime,
      sortOrder: SortOrder.Asc,
    },
    {
      label: 'Total Time (Long To Short)',
      sortField: PlannedRouteSortField.TotalTime,
      sortOrder: SortOrder.Desc,
    },
    {
      label: 'Starting Soon',
      sortField: PlannedRouteSortField.StartDate,
      sortOrder: SortOrder.Asc,
    },
    {
      label: 'Starting Latest',
      sortField: PlannedRouteSortField.StartDate,
      sortOrder: SortOrder.Desc,
    },
  ];

  public routes: IRouteRecommendation[] = [];
  public selectedRouteItem: IRouteRecommendation;

  carryOverOptions: SelectItem[] = [
    { label: 'Include Carryovers', value: false },
    { label: 'Exclude Carryovers', value: true },
  ];

  numberOfTaskOptions: SelectItem[] = [
    { label: 'Any Number of Tasks', value: null },
    { label: 'Max 1 Task in Route', value: 1 },
    { label: 'Max 2 Tasks in Route', value: 2 },
    { label: 'Max 3 Tasks in Route', value: 3 },
    { label: 'Max 4 Tasks in Route', value: 4 },
  ];

  extenderOptions: SelectItem[] = [
    { label: 'All Route Types', value: PlannedRouteExtenderFilter.AllRoutes },
    { label: 'Only Extension Routes', value: PlannedRouteExtenderFilter.OnlyExtensions },
    { label: 'Only Regular Routes', value: PlannedRouteExtenderFilter.OnlyRoutes },
  ];

  clashFilterOptions: SelectItem[] = [
    { label: 'Clash Filter: Show All', value: [] },
    {
      label: 'Exclude Possible Clashes',
      value: [
        RouteScheduleClashSeverity.Definite,
        RouteScheduleClashSeverity.Partial,
        RouteScheduleClashSeverity.Current,
      ],
    },
    {
      label: 'Exclude Likely Clashes',
      value: [RouteScheduleClashSeverity.Definite],
    },
  ];

  filterForm: FormGroup<ControlsOfFlat<IRouteRecommendationSearchFilterValues>>;
  loadingData: boolean;
  routeTasks: MapControlTask[] = [];
  directionColors = ['#0082CB', '#F97316', '#634ECA', '#1B852F', '#111111', '#DC3535'];
  highlightedRoute: number;
  organisations: any[];
  currentOrgId: number;

  first: number = 0;
  routesPerPage: number = 30;

  currentUser: IUser;

  maxDate: Date = new Date();
  minDate: Date = new Date();

  isSupplierPreferencesUpdating: boolean = false;
  isProcessingNewSupplierRoutes: boolean = false;
  initialSearchAttempted: boolean = false;

  systemSettings: ISystemSettings;

  extenderFilterJobOptions: SelectItem[] = [];
  suppliers: SelectItem[] = [];
  private routeSearchDurationStartedAt: number = Date.now();
  private routeSearchExitTracked: boolean = false;

  constructor(
    private routeRecommendationService: RouteRecommendationService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private settingsService: SettingsService,
    private routesAnalyticsService: RoutesAnalyticsService,
    private supplierTravelPreferencesService: SupplierTravelPreferencesService,
  ) {}

  ngOnDestroy(): void {
    this.trackRouteSearchExit();

    this.subscriptions.forEach((subscription) => {
      subscription?.unsubscribe();
    });
  }

  @HostListener('window:beforeunload')
  onWindowBeforeUnload(): void {
    this.trackRouteSearchExit();
  }

  async ngAfterViewInit() {
    this.userService.getLoggedUser().then((user) => {
      this.currentUser = user;
    });

    this.userService.getCurrentUserOrg().then((currentOrg) => {
      this.organisations = [currentOrg];
      this.currentOrgId = currentOrg.id;
    });
  }

  ngOnInit(): void {
    this.initFilters();

    this.resetRouteSearchDuration();

    this.settingsService.GetSystemSettings().then((data: ISystemSettings) => {
      this.systemSettings = data;
    });

    this.maxDate = addDays(new Date(), this.taskScopeDays);
    this.filterForm?.controls.component.setValue(this.routeFilterType);

    if (this.routeFilterType == RouteFilterType.Watched) {
      this.subscriptions.push(
        this.routeRecommendationService.watchedEvent.subscribe((id) => {
          void this.runSearch(true);
        }),
      );
      this.subscriptions.push(
        this.routeRecommendationService.hiddenEvent.subscribe((id) => {
          const removed = remove(this.routes, (x) => x.id === id);
          const newTotal = this.total - removed.length;
          this.totalChange.emit(newTotal);
        }),
      );
    }

    if (this.routeFilterType == RouteFilterType.Hidden) {
      this.subscriptions.push(
        this.routeRecommendationService.hiddenEvent.subscribe((id) => {
          void this.runSearch(true);
        }),
      );
      this.subscriptions.push(
        this.routeRecommendationService.unHiddenEvent.subscribe((id) => {
          const removed = remove(this.routes, (x) => x.id === id);
          const newTotal = this.total - removed.length;
          this.totalChange.emit(newTotal);
        }),
      );
    }

    if (this.routeFilterType == RouteFilterType.Available) {
      this.subscriptions.push(
        this.routeRecommendationService.hiddenEvent.subscribe((id) => {
          const removed = remove(this.routes, (x) => x.id === id);
          const newTotal = this.total - removed.length;
          this.totalChange.emit(newTotal);
        }),
      );
      this.subscriptions.push(
        this.routeRecommendationService.unHiddenEvent.subscribe((id) => {
          void this.runSearch(true);
        }),
      );
    }
    this.subscriptions.push(
      this.routeRecommendationService.refreshSearchEvent.subscribe((id) => {
        void this.runSearch(true);
      }),
    );

    if (this.isVME) this.getSupplierDropdownItems();

    if (!this.isVME) this.getExtenderFilterJobOptions();

    this.setSortFieldForGrabbed();

    this.loadQueryParamFilters();

    if (
      !this.isVME ||
      (this.isVME && this.filterForm.controls.jobNumberOrVehicleReg != undefined)
    ) {
      void this.runSearch(true, true);
    }

    this.checkIfSupplierPreferencesIsUpdating();

    this.checkIfProcessingNewSupplierRoutes();
  }

  async onRouteClick(routeId: number) {
    this.setHighlightedRoute(routeId);
    const clickedRoutes = (await localforage.getItem<number[]>(ClickedRoutesStorageKey)) || [];
    await localforage.setItem<number[]>(ClickedRoutesStorageKey, clickedRoutes.concat([routeId]));

    const currentRoute = this.routes.find((x) => x.id == routeId);

    if (currentRoute == null) {
      return;
    }

    currentRoute.isNew = false;
  }

  setHighlightedRoute(routeId: number) {
    if (this.highlightedRoute == routeId) {
      return;
    }
    this.highlightedRoute = routeId;
    this.updateMapTasks(this.highlightedRoute);
  }

  highlightFirstInList() {
    this.setHighlightedRoute(this.routes.length > 0 ? this.routes[0].id : null);
  }

  async runSearch(fromStart: boolean, track: boolean = false) {
    if (
      this.isVME &&
      !this.formControls.supplierId.value &&
      !this.formControls.jobNumberOrVehicleReg.value
    ) {
      return;
    }

    if (this.loadingData) {
      return;
    }

    this.initialSearchAttempted = true;

    this.setQueryParamFilters();

    try {
      if (fromStart) {
        this.first = 0;
      }

      let filters = this.toFilterCommand(this.filterForm);

      if (this.first == 0) {
        this.routes = [];
      }

      this.loadingData = true;

      const request: IRouteRecommendationSearch = {
        filters,
        sortField: this.formControls.sortBy.value.sortField,
        sortOrder: this.formControls.sortBy.value.sortOrder,
        first: this.first,
        rows: this.routesPerPage,
      };

      const clickedRoutes = (await localforage.getItem<number[]>(ClickedRoutesStorageKey)) || [];

      const { routes, total } = await this.routeRecommendationService.search(request);

      if (track) {
        const pageStayDurationSeconds = this.getRouteSearchDurationSeconds();
        this.routesAnalyticsService.sendRouteSearchEvent(
          request,
          total,
          pageStayDurationSeconds,
          false,
          routes.map((r) => r.id),
        );
        this.resetRouteSearchDuration();
      }
      this.total = total;
      this.first += this.routesPerPage;

      routes.forEach((item) => {
        item.isNew = !clickedRoutes.includes(item.id);
      });

      this.routes = this.routes.concat(...routes);
      if (this.first == 0) {
        this.highlightFirstInList();
      }
    } catch (err) {
      console.error(err);
    } finally {
      this.loadingData = false;
    }
  }

  onSearchByReg(reg: string): void {
    this.formControls.jobNumberOrVehicleReg.setValue(reg);
    void this.runSearch(true, true);
  }

  resetFilters() {
    this.initialSearchAttempted = false;
    this.initFilters();
    this.setSortFieldForGrabbed();
    this.setQueryParamFilters();
    this.routes = [];
  }

  private getRouteSearchDurationSeconds(): number {
    return Math.max(0, Math.round((Date.now() - this.routeSearchDurationStartedAt) / 1000));
  }

  private resetRouteSearchDuration(): void {
    this.routeSearchDurationStartedAt = Date.now();
    this.routeSearchExitTracked = false;
  }

  private trackRouteSearchExit(): void {
    if (this.routeSearchExitTracked || !this.initialSearchAttempted) {
      return;
    }

    if (
      this.isVME &&
      !this.formControls.supplierId.value &&
      !this.formControls.jobNumberOrVehicleReg.value
    ) {
      return;
    }

    const request: IRouteRecommendationSearch = {
      filters: this.toFilterCommand(this.filterForm),
      sortField: this.formControls.sortBy.value.sortField,
      sortOrder: this.formControls.sortBy.value.sortOrder,
      first: this.first,
      rows: this.routesPerPage,
    };

    this.routesAnalyticsService.sendRouteSearchEvent(
      request,
      this.total,
      this.getRouteSearchDurationSeconds(),
      true,
    );
    this.routeSearchExitTracked = true;
  }

  initFilters() {
    this.filterForm = new FormGroup<ControlsOfFlat<IRouteRecommendationSearchFilterValues>>({
      dateRange: new FormControl(null),
      noCarryOvers: new FormControl<boolean>(false),
      dedupeRoutes: new FormControl<boolean>(false),
      maxNumberOfTasks: new FormControl<number>(null),
      component: new FormControl<RouteFilterType>(this.routeFilterType),
      sortBy: new FormControl<PlannedRouteSortFieldValue>(this.sortByOptions[0]),
      extender: new FormControl<number>(PlannedRouteExtenderFilter.AllRoutes),
      extenderSelectedJobs: new FormControl<number[]>(null),
      supplierId: new FormControl<number>(null),
      jobNumberOrVehicleReg: new FormControl<string>(''),
      excludeClashSeverities: new FormControl<RouteScheduleClashSeverity[]>(
        this.isVME ? [RouteScheduleClashSeverity.Definite] : [],
      ),
    });
  }

  private toFilterCommand(
    formGroup: FormGroup<ControlsOfFlat<IRouteRecommendationSearchFilterValues>>,
  ): IRouteRecommendationSearchFilters {
    const values = formGroup.getRawValue();
    return {
      maxNumberOfTasks: { value: values.maxNumberOfTasks },
      dateRange: { value: values.dateRange },
      noCarryOvers: { value: values.noCarryOvers },
      component: { value: values.component },
      extender: { value: values.extender },
      extenderSelectedJobs: { value: values.extenderSelectedJobs },
      supplierId: { value: values.supplierId },
      jobNumberOrVehicleReg: { value: values.jobNumberOrVehicleReg },
      excludeClashSeverities: { value: values.excludeClashSeverities ?? [] },
    };
  }

  private updateMapTasks(routeId: number) {
    const currentRoute = this.routes.find((x) => x.id == routeId);

    if (currentRoute == null) {
      return;
    }

    this.routeTasks = currentRoute.recommendationTasks.map((x, i) => ({
      map: {
        taskId: x.task.id,
        directions: { opacity: 0.7, color: this.directionColors[i] },
      },
      ...x.task,
    }));
  }

  async onConfirmHide(hideRouteSelection: IRouteHiddenReasonSelection) {
    const request: ISetRouteHidden = {
      routeHiddenComment: hideRouteSelection.routeHiddenComment,
      routeHiddenReasonId: hideRouteSelection.routeHiddenReasonId,
      hidden: true,
      routeRecommendationId: this.routeToHideId,
    };

    this.routeRecommendationService.setHidden(request).then(() => {
      this.routesAnalyticsService.sendHideRouteEvent(this.selectedRouteItem, request);
      this.showHideReasonDialog = false;
    });
  }

  onHideRouteClicked($event: number, routeItem: IRouteRecommendation) {
    this.routeToHideId = $event;
    this.selectedRouteItem = routeItem;
    this.showHideReasonDialog = true;
  }

  onChangedVisibility(isVisible: boolean, id: number) {
    if (this.routes.length > 0 && isVisible) {
      const lastRoute = this.routes[this.routes.length - 1];

      if (id == lastRoute.id && this.routes.length < this.total) {
        if (!this.loadingData) {
          void this.runSearch(false, true);
        }
      }
    }
  }

  setQueryParamFilters(): void {
    this.route.queryParams.pipe(first()).subscribe((params) => {
      if (
        !params['plannedRouteFilterTab'] ||
        params['plannedRouteFilterTab'] == this.routeFilterType
      ) {
        const formValues = this.filterForm.value;
        const queryParams: any = {};

        if (formValues.noCarryOvers !== null) {
          queryParams.noCarryOvers = formValues.noCarryOvers;
        }

        if (formValues.dateRange && formValues.dateRange.length > 0) {
          const dateRange = formValues.dateRange.map((date) => moment(date).format('DD-MM-YYYY'));

          queryParams.dateRange = dateRange.join(',');
        } else {
          queryParams.dateRange = '';
        }

        if (formValues.maxNumberOfTasks !== null) {
          queryParams.maxNumberOfTasks = formValues.maxNumberOfTasks;
        } else {
          queryParams.maxNumberOfTasks = '';
        }

        if (this.formControls.sortBy.value) {
          queryParams.sortOrder = this.formControls.sortBy.value.sortOrder;
          queryParams.sortField = this.formControls.sortBy.value.sortField;
        }

        if (formValues.extender !== null) {
          queryParams.extender = formValues.extender;
        }

        if (formValues.jobNumberOrVehicleReg !== null) {
          queryParams.jobNumberOrVehicleReg = formValues.jobNumberOrVehicleReg;
        }

        if (this.isVME) {
          queryParams.supplierId = formValues.supplierId;
        }

        if (formValues.excludeClashSeverities && formValues.excludeClashSeverities.length > 0) {
          queryParams.excludeClashSeverities = formValues.excludeClashSeverities.join(',');
        } else {
          queryParams.excludeClashSeverities = '';
        }

        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: queryParams,
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  loadQueryParamFilters(): void {
    this.route.queryParams.pipe(first()).subscribe((params) => {
      if (
        !params['plannedRouteFilterTab'] ||
        params['plannedRouteFilterTab'] == this.routeFilterType
      ) {
        if (params['noCarryOvers'] !== undefined) {
          this.filterForm.controls.noCarryOvers.setValue(params['noCarryOvers'] === 'true');
        }

        if (params['dateRange']) {
          const dateStrings = params['dateRange'].split(',');

          const dateRange = dateStrings.map((dateString) =>
            moment(dateString, 'DD-MM-YYYY').toDate(),
          );

          this.filterForm.controls.dateRange.setValue(dateRange);
        }

        if (params['maxNumberOfTasks']) {
          this.filterForm.controls.maxNumberOfTasks.setValue(+params['maxNumberOfTasks']);
        }

        if (params['sortField'] && params['sortOrder']) {
          this.formControls.sortBy.setValue(
            this.sortByOptions.find(
              (option) =>
                option.sortField === params['sortField'] &&
                option.sortOrder === +params['sortOrder'],
            ),
          );
        }

        if (params['extender'] !== undefined) {
          this.filterForm.controls.extender.setValue(+params['extender']);
        }

        if (params['supplierId'] !== undefined && this.isVME) {
          this.filterForm.controls.supplierId.setValue(+params['supplierId']);
        }

        if (params['jobNumberOrVehicleReg']) {
          this.filterForm.controls.jobNumberOrVehicleReg.setValue(params['jobNumberOrVehicleReg']);
        }

        if (params['excludeClashSeverities']) {
          const severities = (params['excludeClashSeverities'] as string)
            .split(',')
            .filter((x) => x.length > 0) as RouteScheduleClashSeverity[];
          const match = this.clashFilterOptions.find(
            (x) =>
              (x.value as RouteScheduleClashSeverity[]).length === severities.length &&
              (x.value as RouteScheduleClashSeverity[]).every((y) => severities.includes(y)),
          );
          this.filterForm.controls.excludeClashSeverities.setValue(
            (match?.value as RouteScheduleClashSeverity[]) ?? severities,
          );
        }

        if (this.isVME && !params['excludeClashSeverities']) {
          this.filterForm.controls.excludeClashSeverities.setValue(null);
        }
      }
    });
  }

  setSortFieldForGrabbed(): void {
    if (this.routeFilterType == RouteFilterType.Grabbed) {
      this.formControls.sortBy.setValue(
        this.sortByOptions.find(
          (x) => x.sortField == PlannedRouteSortField.StartDate && x.sortOrder == SortOrder.Desc,
        ),
      );
    }
  }

  checkIfSupplierPreferencesIsUpdating(): void {
    this.supplierTravelPreferencesService.isSupplierPreferencesUpdating().then((result) => {
      this.isSupplierPreferencesUpdating = result;
    });
  }

  public externalReload(): void {
    this.checkIfSupplierPreferencesIsUpdating();
    void this.runSearch(true);
  }

  checkIfProcessingNewSupplierRoutes(): void {
    this.routeRecommendationService.isProcessingNewSupplierRoutes().then((result) => {
      this.isProcessingNewSupplierRoutes = result;
    });
  }

  get formControls() {
    return this.filterForm.controls;
  }

  resetDateOfJob() {
    this.filterForm.patchValue({ dateRange: null });
  }

  getExtenderFilterJobOptions(): void {
    this.routeRecommendationService.getExtenderFilterOptions().then((result) => {
      this.extenderFilterJobOptions = result.map((filterItem) => ({
        label: filterItem.label,
        value: filterItem.jobId,
        disabled: !filterItem.isInScope,
      }));
    });
  }

  getSupplierDropdownItems(): void {
    this.routeRecommendationService.getSupplierDropdownItems().then((result) => {
      this.suppliers = result;
    });
  }

  public get showVmePrompt(): boolean {
    return (
      this.isVME &&
      ((!this.formControls.supplierId.value && !this.formControls.jobNumberOrVehicleReg.value) ||
        !this.initialSearchAttempted) &&
      !this.loadingData &&
      this.routes.length === 0
    );
  }

  onSupplierChanged(event: { value: number }): void {
    if (!event.value) {
      this.initialSearchAttempted = false;
      this.setQueryParamFilters();
    }
  }

  joinValues(values: any[]): string {
    return values
      .map((v) => this.extenderFilterJobOptions.find((o) => o.value === v)?.label)
      .join(' & ');
  }

  protected readonly customCalendarCellClass = customCalendarCellClass;
}
